/**
 * The fields of one record as label–value rows: a definition list, labels in
 * one column and values in the other.
 *
 * An empty value — null, undefined, `''` or `false` — shows the translated
 * dash, so a missing field reads as "none" rather than as a rendering fault,
 * and a page can write `record.phone && <Link …/>` without a fallback.
 */
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export interface DetailsProps {
  /** `[label, value]` pairs, in order. Labels must be unique: they are the React keys. */
  rows: ReadonlyArray<readonly [string, ReactNode]>;
}

export function Details({ rows }: DetailsProps) {
  const { t } = useTranslation('common');
  return (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 1, m: 0 }}>
      {rows.map(([label, value]) => (
        <Fragment key={label}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {value === null || value === undefined || value === '' || value === false ? t('state.empty') : value}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}
